import DateUtils from 'utils/dateUtils';
import { MemberPayRunStatus, PayRunFrequencyType, PayRunStatus, SuggestedAdjustmentType } from './constants';
import { JobReviewStatus } from './constants';

export const getFormattedPayPeriodDate = (payRunFrom: string, payRunTo: string) => {
  if (!payRunFrom || !payRunTo) {
    return '';
  }
  const fromDate = DateUtils.getDateInFormat({ date: new Date(payRunFrom), dateFormat: 'MMM dd, yyyy' });
  const toDate = DateUtils.getDateInFormat({ date: new Date(payRunTo), dateFormat: 'MMM dd, yyyy' });
  return `${fromDate} - ${toDate}`;
};

export const getMemberPayRunStatusMap = (memberPayRunStatus: string) => {
  switch (memberPayRunStatus) {
    case MemberPayRunStatus.PENDING_APPROVAL:
      return {
        text: '#F2994A',
        bg: '#FFF1D9',
        border: '#F2994A33',
        label: 'Pending approval'
      };
    case MemberPayRunStatus.APPROVED:
    case MemberPayRunStatus.APPROVED_FOR_PAY:
      return {
        text: '#4A8DF2',
        bg: '#E8F0FF',
        border: '#4A8DF233',
        label: 'Approved for pay'
      };
    case MemberPayRunStatus.PAID:
      return {
        text: '#27AE60',
        bg: '#E8F9F1',
        border: '#27AE6033',
        label: 'Paid'
      };
    default:
      return {
        text: '#ACC1D9',
        bg: '#F5F5F5',
        border: '#ACC1D933',
        label: memberPayRunStatus.toLowerCase()
      };
  }
};

export const getJobReviewStatusMap = (jobReviewStatus: string) => {
  switch (jobReviewStatus) {
    case JobReviewStatus.NEEDS_REVIEW:
      return {
        text: '#4A8DF2', // Blue
        bg: '#DEF1FF',
        border: '#4A8DF2',
        label: 'Needs review'
      };
    case JobReviewStatus.IN_PROGRESS:
      return {
        text: '#F2994A', // Orange
        bg: '#FFF1D9',
        border: '#F2994A',
        label: 'In progress'
      };
    case JobReviewStatus.CONFIRMED:
      return {
        text: '#27AE60', // Green
        bg: '#E8F9F1',
        border: '#27AE60',
        label: 'Confirmed'
      };
    case JobReviewStatus.SKIPPED:
      return {
        text: '#878787', // Gray
        bg: '#F5F5F5',
        border: '#E0E0E0',
        label: 'Skipped from pay'
      };
    default:
      return {
        text: '#878787',
        bg: '#F5F5F5',
        border: '#E0E0E0',
        label: jobReviewStatus?.toLowerCase()
      };
  }
};

export const getSuggestedAdjustmentTitle = (type: SuggestedAdjustmentType) => {
  switch (type) {
    case SuggestedAdjustmentType.OTHER:
      return 'Payment collected offline';
    case SuggestedAdjustmentType.TIP:
      return 'Tip received';
    case SuggestedAdjustmentType.REFUND:
      return 'Payment refunded';
    case SuggestedAdjustmentType.DISPUTE:
      return 'Dispute by customer';
    default:
      return '';
  }
};

export const getCustomDuration = (frequency: PayRunFrequencyType): number => {
  switch (frequency) {
    case PayRunFrequencyType.WEEKLY:
      return 7;
    case PayRunFrequencyType.EVERY_TWO_WEEKS:
      return 14;
    case PayRunFrequencyType.MONTHLY:
      return 30;
    case PayRunFrequencyType.TWICE_A_MONTH:
      return 15;
    default:
      return 7;
  }
};

export const getPayrunStatusText = (status: PayRunStatus): string => {
  switch (status.toUpperCase()) {
    case PayRunStatus.OPEN:
    case PayRunStatus.PENDING:
      return 'Open';
    case PayRunStatus.COMPLETE:
    case PayRunStatus.APPROVED:
      return 'Complete';
    default:
      return status.toLowerCase();
  }
};

export const getPayrunStatusColor = (status: PayRunStatus): string => {
  switch (status.toUpperCase()) {
    case PayRunStatus.OPEN:
    case PayRunStatus.PENDING:
      return 'text-pending-orange';
    case PayRunStatus.COMPLETE:
    case PayRunStatus.APPROVED:
      return 'text-successHighlight';
    case PayRunStatus.FAILED:
      return 'text-error';
    default:
      return 'text-heading';
  }
};

export const getPayRunPeriodInEST = ({
  payRunFrom,
  payRunTo,
  dateFormat = 'MMM dd, yyyy',
  showTimeZone = true
}: {
  payRunFrom: string;
  payRunTo: string;
  dateFormat?: string;
  showTimeZone?: boolean;
}) => {
  if (!payRunFrom || !payRunTo) {
    return '';
  }
  const fromDate = DateUtils.formatTimeWithTimeZone({
    date: new Date(payRunFrom),
    dateFormat,
    timeZone: 'America/New_York'
  });
  const toDate = DateUtils.formatTimeWithTimeZone({
    date: new Date(payRunTo),
    dateFormat,
    timeZone: 'America/New_York'
  });
  return `${fromDate} - ${toDate} ${showTimeZone ? '(EST)' : ''}`;
};

export const getSortTooltipText = (
  columnId: string,
  currentSortBy: string,
  currentSortOrder: string,
  isTextColumn = false
): string => {
  const isActiveSort = currentSortBy === columnId;

  if (isTextColumn) {
    // Special case for text-based columns (names, titles, etc.)
    if (isActiveSort) {
      return currentSortOrder.toUpperCase() === 'ASC' ? 'Sorted A-Z' : 'Sorted Z-A';
    }
    return 'Sort A-Z';
  }
  // For numeric/date columns
  if (isActiveSort) {
    return currentSortOrder.toUpperCase() === 'ASC' ? 'Sorted ascending' : 'Sorted descending';
  }
  return 'Sort ascending';
};
